import axios from 'axios';
import { handleAuthError } from '../funciones/authHelper';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Fase B · §4.5 — las dos peticiones de la exportación.
 *
 * ── QUÉ CAMPOS EXISTEN LO DICE EL SERVIDOR ────────────────────────────────────────────────
 *
 * El selector NO lleva la lista escrita. La pide, y el servidor la resuelve desde el token: quien
 * no es Super Administrador **no recibe los personales**, así que no puede ni verlos ausentes —
 * sencillamente no llegan.
 *
 * Que exista este endpoint no relaja nada: el servidor **vuelve a validar** al exportar. Si algún
 * día las dos listas se separasen, la que manda sigue siendo la del servidor. Es justo el modo de
 * fallo que costó la Fase 11 y el ENUM de §4.3 — una lista blanca copiada a mano en dos sitios.
 */
export const fetchCamposExportables = async (listado = 'personas') => {
  try {
    const response = await axios.get(`${API_URL}/api/exportacion/campos`, {
      params: { listado },
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

/**
 * Pide el fichero y lo devuelve como `blob`.
 *
 * ── EL CUERPO ES EL DEL LISTADO, NO EL DEL VCF ────────────────────────────────────────────
 *
 * `negocio`, no `empresa`; `segundo_factor`, no `verificacion`. Los dos nombres existen en el
 * proyecto y **ninguno falla cuando no casa**: el parámetro sencillamente no llega y el filtro no
 * se aplica, así que el fichero saldría con las 270 filas en vez de las 147.
 *
 * Y **sin paginación**: se exporta lo que el filtro selecciona, no la página visible.
 *
 * ── `responseType: 'blob'` NO ES OPCIONAL ─────────────────────────────────────────────────
 *
 * Sin él, axios interpreta el `.xlsx` como texto y lo corrompe: el fichero se descarga, pesa lo
 * que debe, y Excel dice que está dañado. El error aparece al abrirlo, no al descargarlo.
 *
 * Por eso el rechazo del servidor —que viene en JSON— hay que LEERLO DEL BLOB: con `blob` activo,
 * `error.response.data` es un Blob aunque el cuerpo sea JSON. Eso lo resuelve `mensajeDeError`.
 */
export const fetchExportarXLSX = async (listado, filtros = {}, campos = null) => {
  const ruta = listado === 'colaboradoras'
    ? `${API_URL}/api/personas-colaboradoras/exportar/xlsx`
    : `${API_URL}/api/personas/exportar/xlsx`;

  const response = await axios.post(ruta,
    { ...filtros, ...(campos && campos.length ? { campos } : {}) },
    {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      responseType: 'blob',
    });
  return response.data;
};

/* ══════════════════════════════════════════════════════════════════════════════════════
 * EL EMBUDO DE INCORPORACIONES
 *
 * Van aquí y no en un fichero nuevo: las preguntas difíciles de exportar —leer un rechazo que
 * viene dentro de un Blob, descargar sin dejar el objeto colgado, el `responseType`— ya están
 * contestadas en este fichero, y `mensajeDeError` y `descargarBlob` se reutilizan tal cual.
 *
 * ── LOS FILTROS LLEGAN YA TRADUCIDOS, POR LA MISMA FUNCIÓN QUE LOS DEL LISTADO ─────────
 *
 * `filtros` es lo que devuelve `filtrosDelEmbudo(location.search)`, que es **la única**
 * traducción entre la URL y el servidor y la que usa también el listado. Esta función no
 * interpreta nada: pone lo que le dan en la query.
 *
 * La primera versión mandaba `location.search` tal cual, con el argumento de que así no había dos
 * listas de nombres que separar. Los NOMBRES casaban; **el valor del ámbito, no** —la URL dice
 * `colaboradores` y el servidor entiende `colaborador`—, y el servidor lo descartaba en silencio:
 * el fichero bajaba 11 filas mientras el botón anunciaba 2.
 *
 * Es exactamente la deuda de arriba —`verificacion` contra `segundo_factor`— en su variante
 * difícil de ver: **el nombre es lo que se revisa, y el que fallaba era el valor.**
 */
export const fetchCamposIncorporaciones = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/incorporaciones/exportar/campos`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const fetchExportarIncorporacionesXLSX = async (filtros = {}, campos = null) => {
  // Sin `page`: se exporta lo que el filtro selecciona, no la página visible. Y `filtros` ya es
  // una lista blanca, así que `pagina` y `alta` no pueden colarse —no hay nada que quitar—.
  const params = new URLSearchParams(filtros);

  const response = await axios.post(
    `${API_URL}/api/incorporaciones/exportar/xlsx${params.toString() ? `?${params}` : ''}`,
    { ...(campos && campos.length ? { campos } : {}) },
    {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      responseType: 'blob',
    });
  return response.data;
};

/** Bajas. Mismo mecanismo y misma forma; el catálogo no depende del rol porque no hay nada personal. */
export const fetchCamposBajas = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/bajas/exportar/campos`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const fetchExportarBajasXLSX = async (filtros = {}, campos = null) => {
  const params = new URLSearchParams(filtros);
  const response = await axios.post(
    `${API_URL}/api/bajas/exportar/xlsx${params.toString() ? `?${params}` : ''}`,
    { ...(campos && campos.length ? { campos } : {}) },
    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob' });
  return response.data;
};

/**
 * Saca el mensaje del servidor cuando el error viaja dentro de un Blob.
 *
 * Sin esto, un rechazo de campo no permitido se enseñaría como «Error al exportar» genérico, y el
 * usuario no sabría cuál de los campos que marcó es el problema.
 */
export const mensajeDeError = async (error, porDefecto = 'No se ha podido generar el fichero.') => {
  const datos = error?.response?.data;
  if (!datos) return porDefecto;
  try {
    const texto = typeof datos.text === 'function' ? await datos.text() : JSON.stringify(datos);
    const json = JSON.parse(texto);
    return json.message || porDefecto;
  } catch (e) {
    return porDefecto;
  }
};

/**
 * La descarga en sí. Copiado del patrón que ya usa `GestorAprobaciones`: `createObjectURL`, un
 * `<a download>` efímero, y **`revokeObjectURL` al final** — sin eso el blob se queda en memoria
 * hasta recargar la página, y aquí puede pesar 270 filas.
 */
export const descargarBlob = (blob, nombre) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombre;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** `personas_2026-09-23.xlsx` — el mismo nombre que pone el servidor en el Content-Disposition. */
export const nombreFichero = (listado) => {
  const hoy = new Date();
  const fecha = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
  // Un mapa y no un ternario encadenado: el tercer listado entra AQUI, donde ya se contesta esta
  // pregunta, en vez de en un `if` al lado. Lo desconocido sigue cayendo en `personas`, como antes.
  const BASE = { colaboradoras: 'personas-colaboradoras', incorporaciones: 'incorporaciones', bajas: 'bajas' };
  return `${BASE[listado] || 'personas'}_${fecha}.xlsx`;
};
